import { useState } from "react"
import { PlusCircle, BookOpen } from "lucide-react"
import { Button } from "../components/ui/button"
import { SchoolSidebar } from "../components/ui/school/school-dashboard/school-sidebar"
import { SubjectStats } from "../components/ui/school/subject-management/subject-stats"
import { SubjectTable } from "../components/ui/school/subject-management/subject-table"
import { AddSubjectModal } from "../components/ui/school/subject-management/add-subject-modal"
import { EditSubjectModal } from "../components/ui/school/subject-management/edit-subject-modal"
import { DeleteConfirmModal } from "../components/ui/school/subject-management/delete-confirm-modal"
import { initialSubjects } from "../lib/subject-data"

const SubjectManagementPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [subjects, setSubjects] = useState(initialSubjects)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedSubject, setSelectedSubject] = useState(null)
  const [deleteSubjectId, setDeleteSubjectId] = useState("")

  // Calculate stats
  const totalSubjects = subjects.length
  const activeTeachers = new Set(subjects.map((s) => s.teacher)).size
  const totalEnrollment = subjects.reduce((sum, s) => sum + s.enrolled, 0)

  const handleAddSubject = (newSubject) => {
    const subject = {
      ...newSubject,
      id: (subjects.length + 1).toString(),
    }
    setSubjects([...subjects, subject])
  }

  const handleEditSubject = (subject) => {
    setSelectedSubject(subject)
    setIsEditModalOpen(true)
  }

  const handleUpdateSubject = (updatedSubject) => {
    setSubjects(subjects.map((s) => (s.id === updatedSubject.id ? updatedSubject : s)))
  }

  const handleDeleteClick = (id) => {
    setDeleteSubjectId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    setSubjects(subjects.filter((s) => s.id !== deleteSubjectId))
    setIsDeleteModalOpen(false)
    setDeleteSubjectId("")
  }

  const subjectToDelete = subjects.find((s) => s.id === deleteSubjectId)

  return (
    <div className="flex min-h-screen bg-background">
      <SchoolSidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setIsSidebarOpen(false)} />
      )}

      <main className={`flex-1 transition-all duration-300 ${isSidebarOpen ? "md:ml-64" : ""}`}>
        {/* Header */}

        {/* Main Content */}
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div>
              <h1 className="text-3xl font-bold text-secondary mb-2">Subject Management</h1>
              <p className="text-muted-foreground">
                Manage curriculum and course details for the upcoming academic session.
              </p>
            </div>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
            >
              <PlusCircle className="w-5 h-5 mr-2" />
              Add Subject
            </Button>
          </div>

          <SubjectStats
            totalSubjects={totalSubjects}
            activeTeachers={activeTeachers}
            totalEnrollment={totalEnrollment}
          />

          <SubjectTable subjects={subjects} onEdit={handleEditSubject} onDelete={handleDeleteClick} />
        </div>

      </main>

      {/* Modals */}
      <AddSubjectModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddSubject} />

      <EditSubjectModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedSubject(null)
        }}
        onUpdate={handleUpdateSubject}
        subject={selectedSubject}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeleteSubjectId("")
        }}
        onConfirm={handleConfirmDelete}
        subjectName={subjectToDelete?.name || ""}
      />
    </div>
  )
}

export default SubjectManagementPage